import { Injectable, inject } from '@angular/core'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { catchError, exhaustMap, map, of, switchMap, tap, withLatestFrom, mergeMap, first, finalize } from 'rxjs'
import { GbAddProjectorContainerDialogComponent } from 'libs/gb-dashboard/projector/feature/src/lib/gb-add-projector-container-dialog/gb-add-projector-container-dialog.component'
import { gbCheckLoaded } from '@galabid/shared/utils/operators'
import { PresenterConstants } from 'libs/gb-dashboard/presenter/feature/src/lib/presenter.constants'
import { GbNavigationService } from 'libs/shared/utils/services/src/lib/gb-navigation-service/gb-navigation.service'
import { ProjectorContainersApiService } from './projector-containers-api.service'
import * as ProjectorContainersActions from './projector-containers.actions'
import {
  PROJECTOR_CONTAINERS_LOADER,
  ProjectorContainer,
  ProjectorContainerWidgetsResponse,
} from './projector-containers.models'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { GbLoaderService } from '@galabid/shared/utils/loader'
import { ProjectorContainersFacade } from './projector-containers.facade'
import * as PresenterWidgetsActions from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.actions'
import * as ProjectorActions from 'libs/gb-dashboard/projector/data-access/src/lib/+state/projectors.actions'

@Injectable()
export class ProjectorContainersEffects {
  private actions$ = inject(Actions)

  loadProjectorContainers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorContainersActions.loadProjectorContainers),
      withLatestFrom(this.selectedAuctionFacade.urlName$),

      switchMap(([action, urlName]) => {
        return this.projectorContainersApiService.getProjectorContainers(urlName || action.urlName || '').pipe(
          map((response: ProjectorContainer[]) => {
            return ProjectorContainersActions.loadProjectorContainersSuccess({
              projectorContainers: response,
            })
          }),
          catchError((error) => {
            return of(ProjectorContainersActions.loadProjectorContainersFailure({ error }))
          }),
        )
      }),
    ),
  )

  duplicateSlide$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorContainersActions.duplicateSlide),
      tap(() => this.gbLoaderService.showLoaderDialog(PROJECTOR_CONTAINERS_LOADER.duplicateSlide.id)),
      withLatestFrom(this.selectedAuctionFacade.urlName$, this.projectorContainersFacade.selectedProjectorContainer$),
      switchMap(([action, urlName, selectedProjectorContainer]) => {
        return this.projectorContainersApiService
          .duplicateSlide(urlName, selectedProjectorContainer?.uuid || '', action.projectorUuid, action.name)
          .pipe(
            switchMap((projector) => [
              ProjectorActions.addSlideSuccess({ projector }),
              ProjectorActions.selectProjector({ uuid: projector.uuid }),
              ProjectorContainersActions.duplicateSlideSuccess({ projectorUuid: projector.uuid }),
            ]),
            catchError((error) => of(ProjectorContainersActions.duplicateSlideFailure({ error }))),
            finalize(() => this.gbLoaderService.hideLoaderDialog(PROJECTOR_CONTAINERS_LOADER.duplicateSlide.id)),
          )
      }),
    ),
  )

  loadProjectorContainerData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorContainersActions.loadProjectorContainerData),
      gbCheckLoaded(this.selectedAuctionFacade.urlName$),
      mergeMap(() =>
        this.projectorContainersFacade.selectedProjectorContainer$.pipe(
          first((selectedProjectorContainer): selectedProjectorContainer is ProjectorContainer =>
            Boolean(selectedProjectorContainer?.uuid),
          ),
          withLatestFrom(this.selectedAuctionFacade.urlName$),
          switchMap(([selectedProjectorContainer, urlName]) =>
            this.projectorContainersApiService
              .getProjectorContainerWidgets(urlName, selectedProjectorContainer.uuid)
              .pipe(
                mergeMap((widgetsResponse: ProjectorContainerWidgetsResponse) => [
                  PresenterWidgetsActions.loadCountdownWidgetsSuccess({
                    widgets: widgetsResponse.CountDownProjectorWidget || [],
                  }),
                  PresenterWidgetsActions.loadImageWidgetsSuccess({
                    widgets: widgetsResponse.ImageProjectorWidget || [],
                  }),
                  PresenterWidgetsActions.loadTextWidgetsSuccess({
                    widgets: widgetsResponse.TextProjectorWidget || [],
                  }),
                  PresenterWidgetsActions.loadSmartTextWidgetsSuccess({
                    widgets: widgetsResponse.SmartTextProjectorWidget || [],
                  }),
                  PresenterWidgetsActions.loadTotalRaisedWidgetsSuccess({
                    widgets: widgetsResponse.TotalRaisedProjectorWidget || [],
                  }),
                  PresenterWidgetsActions.loadTeamScoringWidgetsSuccess({
                    widgets: widgetsResponse.TeamScoringProjectorWidget || [],
                  }),
                  PresenterWidgetsActions.loadPowerPledgeWidgetsSuccess({
                    widgets: widgetsResponse.PowerPledgeProjectorWidget || [],
                  }),
                  PresenterWidgetsActions.loadNotificationWidgetsSuccess({
                    widgets: widgetsResponse.NotificationProjectorWidget || [],
                  }),
                  PresenterWidgetsActions.loadItemTableWidgetsSuccess({
                    widgets: widgetsResponse.ItemTableProjectorWidget || [],
                  }),
                  PresenterWidgetsActions.loadRaffleWinnersWidgetsSuccess({
                    widgets: widgetsResponse.RaffleWinnersProjectorWidget || [],
                  }),
                  PresenterWidgetsActions.loadHonourRollWidgetsSuccess({
                    widgets: widgetsResponse.HonourRollProjectorWidget || [],
                  }),
                  PresenterWidgetsActions.loadThermometerWidgetsSuccess({
                    widgets: widgetsResponse.ThermometerProjectorWidget || [],
                  }),
                  PresenterWidgetsActions.loadRaffleDrawWidgetsSuccess({
                    widgets: widgetsResponse.RaffleDrawProjectorWidget || [],
                  }),
                  PresenterWidgetsActions.loadQrCodeWidgetsSuccess({
                    widgets: widgetsResponse.QrCodeProjectorWidget || [],
                  }),
                  PresenterWidgetsActions.loadVideoWidgetsSuccess({
                    widgets: widgetsResponse.VideoProjectorWidget || [],
                  }),
                  ProjectorContainersActions.projectorContainerDataLoadedAndReady(),
                ]),
                catchError((error) =>
                  of(
                    ProjectorContainersActions.projectorContainerDataLoadFailure({
                      error,
                    }),
                  ),
                ),
              ),
          ),
        ),
      ),
    ),
  )

  openCreateProjectorDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectorContainersActions.openCreateProjectorDialog),
        exhaustMap(() =>
          this.gbDashboardDialogService.getDialogObs(GbAddProjectorContainerDialogComponent, {
            disableClose: true,
            width: '700px',
            mobileFullScreen: true,
          }),
        ),
      ),
    { dispatch: false },
  )

  createProjectorContainer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorContainersActions.createProjectorContainer),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([{ projectorContainer }, urlName]) => {
        return this.projectorContainersApiService
          .createProjectorContainer(urlName, { ...projectorContainer, auction: urlName })
          .pipe(
            map((response: ProjectorContainer) => {
              return ProjectorContainersActions.createProjectorContainerSuccess({
                projectorContainer: response,
              })
            }),
            catchError((error) => {
              return of(ProjectorContainersActions.createProjectorContainerFailure({ error }))
            }),
          )
      }),
    ),
  )

  closeCreateProjectorDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectorContainersActions.createProjectorContainerSuccess),
        map(() => this.gbDashboardDialogService.closeDialog(GbAddProjectorContainerDialogComponent, true)),
      ),
    { dispatch: false },
  )

  updateProjectorContainer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorContainersActions.updateSelectedProjectorContainer),
      withLatestFrom(this.projectorContainersFacade.selectedProjectorContainer$),
      switchMap(([action, selectedProjectorContainer]) => {
        return this.projectorContainersApiService
          .updateProjectorContainer({ ...selectedProjectorContainer, ...action.projectorContainer })
          .pipe(
            map((response: ProjectorContainer) => {
              return ProjectorContainersActions.updateSelectedProjectorContainerSuccess({
                update: { id: response.uuid, changes: response },
              })
            }),
            catchError((error) => {
              return of(ProjectorContainersActions.updateSelectedProjectorContainerFailure({ error }))
            }),
          )
      }),
    ),
  )

  deleteProjectorContainer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectorContainersActions.deleteProjectorContainer),
      exhaustMap((action) =>
        this.gbDashboardDialogService.filterByConfirmDialog(
          {
            title: 'PROJECTOR.CONFIRM_DELETE_TITLE_CONTAINER',
            body: 'PROJECTOR.CONFIRM_DELETE_BODY_CONTAINER',
            confirmButtonText: 'PROJECTOR.CONFIRM_DELETE_CONFIRM',
            cancelButtonText: 'PROJECTOR.CONFIRM_DELETE_CANCEL',
          },
          action,
        ),
      ),
      tap(() => this.gbLoaderService.showLoaderDialog(PROJECTOR_CONTAINERS_LOADER.deleteProjectorContainer.id)),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([{ uuid }, urlName]) => {
        return this.projectorContainersApiService.deleteProjectorContainer(urlName, uuid).pipe(
          map(() => ProjectorContainersActions.deleteProjectorContainerSuccess({ uuid })),
          catchError((error) => of(ProjectorContainersActions.deleteProjectorContainerFailure({ error }))),
          finalize(() =>
            this.gbLoaderService.hideLoaderDialog(PROJECTOR_CONTAINERS_LOADER.deleteProjectorContainer.id),
          ),
        )
      }),
    ),
  )

  goToPresenter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectorContainersActions.goToPresenter, ProjectorContainersActions.createProjectorContainerSuccess),
        gbCheckLoaded(this.selectedAuctionFacade.urlName$),
        withLatestFrom(this.selectedAuctionFacade.urlName$),
        tap(([action, urlName]) => {
          const uuid: string =
            action.type === ProjectorContainersActions.goToPresenter.type ? action.uuid : action.projectorContainer.uuid
          const fragments = [`/${PresenterConstants.PRESENTER}/${urlName}/${uuid}/${PresenterConstants.EDIT}`]
          this.gbNavigationService.navigate(fragments, { newTab: true })
        }),
      ),
    { dispatch: false },
  )

  launchPresenter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectorContainersActions.launchPresenter),
        gbCheckLoaded(this.selectedAuctionFacade.urlName$),
        withLatestFrom(this.selectedAuctionFacade.urlName$, this.projectorContainersFacade.selectedProjectorContainer$),
        tap(([action, urlName, selectedProjectorContainer]) => {
          const uuid = action.uuid || selectedProjectorContainer?.uuid
          const fragments = [`/${PresenterConstants.PRESENTER}/${urlName}/${uuid}/${PresenterConstants.VIEW}`]
          this.gbNavigationService.navigate(fragments, { newTab: true })
        }),
      ),
    { dispatch: false },
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProjectorContainersActions.loadProjectorContainersFailure,
          ProjectorContainersActions.updateSelectedProjectorContainerFailure,
          ProjectorContainersActions.createProjectorContainerFailure,
          ProjectorContainersActions.deleteProjectorContainerFailure,
          ProjectorContainersActions.duplicateSlideFailure,
          ProjectorContainersActions.projectorContainerDataLoadFailure,
        ),
        map((action) => {
          const error = this.gbErrorMessageService.getErrorMessage(action.error.error)
          if (error && typeof error == 'string') {
            this.gbNotificationService.error(error)
          } else {
            this.gbNotificationService.error('PROJECTOR_CONTAINER_ERROR')
          }
        }),
      ),
    { dispatch: false },
  )

  showSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProjectorContainersActions.createProjectorContainerSuccess,
          ProjectorContainersActions.updateSelectedProjectorContainerSuccess,
          ProjectorContainersActions.deleteProjectorContainerSuccess,
        ),
        tap((action) => {
          if (action.type.includes('create')) {
            this.gbNotificationService.success('PROJECTOR_CONTAINER_CREATE_SUCCESS')
          } else if (action.type.includes('update')) {
            this.gbNotificationService.success('PROJECTOR_CONTAINER_UPDATE_SUCCESS')
          } else if (action.type.includes('delete')) {
            this.gbNotificationService.success('PROJECTOR_CONTAINER_DELETE_SUCCESS')
          }
        }),
      ),
    { dispatch: false },
  )

  constructor(
    private projectorContainersApiService: ProjectorContainersApiService,
    private selectedAuctionFacade: SelectedAuctionFacade,
    private gbNotificationService: GbNotificationService,
    private gbErrorMessageService: GbErrorMessageService,
    private gbNavigationService: GbNavigationService,
    private gbDashboardDialogService: GbDashboardDialogService,
    private gbLoaderService: GbLoaderService,
    private projectorContainersFacade: ProjectorContainersFacade,
  ) {}
}
